(ns ui.components.calculi-tree.view
  (:require
    [common.localization :as loc]
    [common.util :as cu]
    [re-frame.core :as rf]
    [reagent.core :as r]
    [ui.components.button.view :as button]
    [ui.components.calculi-tree.events :as events]
    [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
    [ui.components.calculi-tree.predefined-expressions :as predefined-expressions]
    [ui.editors.data-input :as data-input]
    [ui.editors.labelled-input.view :as labelled-input]
    [ui.components.calculi-tree.formatted-view :as formatted-view]
    [ui.util :as uu]))

(declare render-node)

;; -- Configs --

(def type-options
  [{:value :calculi-type/field :label (loc/tr :calculi-type/field)}
   {:value :calculi-type/operator-multiplication :label (loc/tr :calculi-type/operator-multiplication)}
   {:value :calculi-type/operator-sum :label (loc/tr :calculi-type/operator-sum)}
   {:value :calculi-type/operator-subtraction :label (loc/tr :calculi-type/operator-subtraction)}
   {:value :calculi-type/operator-division :label (loc/tr :calculi-type/operator-division)}
   {:value :calculi-type/parenthesis-group :label (loc/tr :calculi-type/parenthesis-group)}
   {:value :calculi-type/constant :label (loc/tr :calculi-type/constant)}])

;; -- Helpers --

(defn dispatch-set-field [tree-path]
  (fn [v] (rf/dispatch [::events/set-field tree-path v])))

(defn remove-button [tree-path idx]
  [:button.text-xs.text-red-600.ml-2
   {:on-click #(rf/dispatch [::events/remove-child tree-path idx])}
   "✕"])

(defn add-child-button [tree-path]
  [:button.text-xs.text-blue-600.mt-1
   {:on-click #(rf/dispatch [::events/add-child tree-path])}
   "+ Add Child"])


;; Inline editable dropdown for type selection
(defn inline-type-selector [tree-path field-options node]
  [:div.flex.items-center.gap-2
   [dropdown-menu/view
    {:items (concat (->> type-options
                         (cu/remove-by :value :calculi-type/field)
                         (mapv (fn [{:keys [value] :as item}]
                                 (assoc item :on-click
                                             (fn [_]
                                               (rf/dispatch [::events/set-type tree-path value]))))))
                    [{:value :calculi-type/field
                      :label (loc/tr :calculi-type/field)
                      :items (->> field-options
                                  (mapv (fn [{:keys [value] :as item}]
                                          (assoc item :on-click
                                                      (fn [_]
                                                        (rf/dispatch [::events/set-type tree-path :calculi-type/field])
                                                        (rf/dispatch [::events/set-field tree-path value]))))))}])
     :props {:hide-arrow? true
             :align       :end}
     :trigger-as-child
     [:span
      [button/secondary-open
       {:aria-label (loc/tr :select-or-change-operand)}
       [:i {:class "fal fa-edit"}]]]}]])

;; -- Nodes --

(defn field-node [{:keys [field tree-path field-options]}]
  [:div.p-2.bg-white.border.rounded.shadow.flex.items-center
   [labelled-input/label-elem
    {:label (loc/tr :calculi-type-field [(->> field-options
                                              (cu/filter-by :value field)
                                              (first)
                                              :label)])}]
   [:div.ml-2
    [inline-type-selector tree-path field-options {:calculi-type :calculi-type/field}]]])

(defn constant-node [{:keys [value tree-path]}]
  [:div.p-2.bg-white.border.rounded.shadow.flex.items-center.gap-2
   [data-input/view
    {:data-type :data.type/number
     :value     value
     :on-change (fn [new-value]
                  (rf/dispatch [::events/set-constant tree-path new-value]))}]
   [inline-type-selector tree-path nil]])

(defn operator-node [{:keys [calculi-type rules tree-path field-options]}]
  [:div.flex.flex-col.gap-2.p-2.bg-gray-50.border.rounded
   [:div.flex.items-center.gap-2
    [:span.text-sm.text-gray-600 "Operator: " (name calculi-type)]
    [inline-type-selector tree-path field-options {:calculi-type calculi-type}]]
   (doall
     (for [[idx child] (map-indexed vector rules)]
       ^{:key idx}
       [:div.flex.items-start
        [render-node {:node          child
                      :tree-path     (conj tree-path :rules idx)
                      :field-options field-options}]
        [remove-button tree-path idx]]))
   [add-child-button tree-path]])

(defn paren-group-node [{:keys [rules tree-path field-options]}]
  [:div.flex.flex-col.p-2.bg-blue-50.border-l-4.border-blue-400.rounded
   [:div.flex.justify-between.items-center
    [:span.text-xs.text-blue-600 "(...) group"]
    [inline-type-selector tree-path field-options {:calculi-type :calculi-type/parenthesis-group}]]
   (doall
     (for [[idx child] (map-indexed vector rules)]
       ^{:key idx}
       [:div.flex.items-start
        [render-node {:node          child
                      :tree-path     (conj tree-path :rules idx)
                      :field-options field-options}]
        [remove-button tree-path idx]]))
   [add-child-button tree-path]])

(defn render-node [{:keys [field-options tree-path node]}]
  (case (:calculi-type node)
    :calculi-type/field
    [field-node {:field (:field node) :field-options field-options :tree-path tree-path}]

    :calculi-type/constant
    [constant-node {:value (:value node) :tree-path tree-path}]

    :calculi-type/parenthesis-group
    [paren-group-node {:rules (:rules node) :tree-path tree-path :field-options field-options}]

    (:calculi-type/operator-sum
      :calculi-type/operator-subtraction
      :calculi-type/operator-multiplication
      :calculi-type/operator-division)
    [operator-node {:calculi-type  (:calculi-type node)
                    :rules         (:rules node)
                    :tree-path     tree-path
                    :field-options field-options}]

    ;; fallback
    [:div.mt-2
     [:span.text-gray-500.text-sm (loc/tr :select-calculation-type)]
     [inline-type-selector tree-path field-options node]]))

(defn expression-preview [{:keys [node field-options]}]
  (let [field-labels (into {} (map (juxt :value :label) field-options))
        expr (formatted-view/format-expression node field-labels)]
    [:div.mt-4.p-2.bg-yellow-50.border.rounded.text-sm.text-gray-700
     [:span.font-semibold "Expression preview: "]
     [:code
      {:class "expr text-blue-800 font-mono"}
      expr]]))

(defn calculation-builder [{:keys [node tree-path field-options] :as args}]
  (if (:calculi-type node)
    [:<>
     [expression-preview args]
     [render-node args]]
    [labelled-input/view
     {:label       (loc/tr :select-calculation-expression)
      :editor-type :editor.type/select
      :data-type   :data.type/text-simple
      :on-change   (fn [v]
                     (if (= "custom" v)
                       (rf/dispatch [::events/set-type tree-path :calculi-type/operator-division])
                       (let [expression-tree (predefined-expressions/expression->template v field-options)]
                         (js/console.log "Expression tree : " expression-tree)
                         (rf/dispatch [::events/set-expression expression-tree (vec (butlast tree-path))]))))
      :options     (concat (->> predefined-expressions/expressions
                                (keys)
                                (mapv (fn [k]
                                        {:value k
                                         :label k})))
                           [{:value "custom"
                             :label (loc/tr :custom-calculation)}])}]))
